import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cloneDeep, isEqual, set, unset } from 'lodash';
import * as yup from 'yup';

import { FormControlBuffer } from './sources';
import type { DataSource, FormControlConfig, FormControls, FormErrors } from './types';

export type FormControlsWithDataSource<TForm extends object> = FormControls<TForm> & DataSource<TForm>;

// Recursively check whether any leaf in the errors tree is a non-empty string.
function hasAnyError(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.some(hasAnyError);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasAnyError);
  return false;
}

export default function useFormControlsFor<TData, TForm extends object, TPayload = unknown>(
  datasource: DataSource<TData>,
  config: FormControlConfig<TData, TForm, TPayload>
): FormControlsWithDataSource<TForm> {
  const sourceData = datasource.value;
  const sourceLoading = datasource.loading;

  // Stable ref for config to avoid dependency churn (config is typically an object literal)
  const configRef = useRef(config);
  configRef.current = config;

  // Derive initial form values from upstream data
  const initialFormValues = useMemo(() => configRef.current.toFormValues(sourceData), [sourceData]);

  // Internal form state
  const [formValues, setFormValues] = useState<TForm>(() => cloneDeep(initialFormValues));
  const [formErrors, setFormErrors] = useState<FormErrors<TForm>>({} as FormErrors<TForm>);
  const [submitting, setSubmitting] = useState(false);

  // Snapshot for dirty comparison
  const initialRef = useRef<TForm>(cloneDeep(initialFormValues));

  // Re-sync when upstream data changes (e.g. query refetch after save)
  useEffect(() => {
    if (!isEqual(initialRef.current, initialFormValues)) {
      initialRef.current = cloneDeep(initialFormValues);
      setFormValues(cloneDeep(initialFormValues));
      setFormErrors({});
    }
  }, [initialFormValues]);

  const dirty = useMemo(
    () => configRef.current.initiallyDirty || !isEqual(initialRef.current, formValues),
    [formValues]
  );

  // --- Validation ---

  const validateField = useCallback(
    async (name: string, values?: TForm): Promise<FormErrors<TForm>> => {
      const vals = values ?? formValues;
      const errors: Record<string, string> = {};
      const { schema } = configRef.current;

      if (!schema) return errors as FormErrors<TForm>;

      // Validate the leaf path plus any array-item ancestor paths, so that
      // item-level yup .test() rules (e.g. "either A or B is required") fire
      // when a child field changes.
      const pathsToValidate = [name];
      const itemAncestorRegex = /^(.*\[\d+\])\./;
      let match = name.match(itemAncestorRegex);
      while (match) {
        pathsToValidate.push(match[1]);
        match = match[1].match(itemAncestorRegex);
      }

      const results = await Promise.all(
        pathsToValidate.map(async (path) => {
          try {
            await schema.validateAt(path, vals);
            return { path, msg: null as string | null };
          } catch (error) {
            if (yup.ValidationError.isError(error)) {
              return { path, msg: error.message };
            }
            return { path, msg: null };
          }
        })
      );

      // Apply all path updates in a single setState to avoid a re-render per path.
      setFormErrors((prev) => {
        const next = cloneDeep(prev) as object;
        let changed = false;
        for (const { path, msg } of results) {
          if (msg === null) {
            unset(next, path);
          } else {
            set(next, path, msg);
            set(errors, path, msg);
          }
          changed = true;
        }
        return changed ? (next as FormErrors<TForm>) : prev;
      });

      return errors as FormErrors<TForm>;
    },
    [formValues]
  );

  const validateForm = useCallback(async (): Promise<FormErrors<TForm>> => {
    const errors: Record<string, string> = {};
    const { schema, validate } = configRef.current;

    if (schema) {
      try {
        await schema.validate(formValues, { abortEarly: false });
      } catch (error) {
        if (yup.ValidationError.isError(error)) {
          error.inner.forEach((e) => {
            if (e.path) set(errors, e.path, e.message);
          });
        }
      }
    }

    if (validate) {
      Object.assign(errors, validate(formValues));
    }

    setFormErrors({ ...errors } as FormErrors<TForm>);
    return errors as FormErrors<TForm>;
  }, [formValues]);

  // Derived valid state: dirty AND no tracked errors. We deliberately do NOT
  // re-run the full schema on every value change — that's a render-cascade
  // hazard. submit() runs validateForm() before posting, so anything not yet
  // surfaced via per-field validation gets caught at submit time.
  const formIsValid = useMemo(() => {
    if (!dirty) return false;
    return !hasAnyError(formErrors);
  }, [dirty, formErrors]);

  // --- Mutators ---

  const setValue = useCallback(
    (key: string, value: any, options: { validate?: boolean } = { validate: true }) => {
      setFormValues((prev) => {
        const updated = set({ ...prev }, key, value) as TForm;
        if (options.validate) validateField(key, updated);
        return { ...updated };
      });
    },
    [validateField]
  );

  const setValues = useCallback(
    (values: Partial<TForm>, options: { validate?: boolean } = { validate: true }) => {
      setFormValues((prev) => {
        const updated = { ...prev, ...values } as TForm;
        if (options.validate) {
          for (const key in values) {
            validateField(key, updated);
          }
        }
        return { ...updated };
      });
    },
    [validateField]
  );

  // --- Submit / Cancel ---

  const submit = useCallback(async () => {
    if (submitting) return;

    const errors = await validateForm();
    if (Object.keys(errors).length) return;

    setSubmitting(true);
    try {
      const payload = configRef.current.toPayload(formValues);
      await datasource.save(payload);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, validateForm, formValues, datasource]);

  const cancel = useCallback(() => {
    setFormValues(cloneDeep(initialRef.current));
    setFormErrors({});
  }, []);

  // --- FormControlBuffer (DataSource backed by form state) ---

  const bufferRef = useRef<FormControlBuffer<TForm>>(null!);
  if (bufferRef.current === null) {
    bufferRef.current = new FormControlBuffer<TForm>();
  }

  bufferRef.current._syncState({
    formValues,
    loading: sourceLoading,
    onUpdate: (updatedValues: TForm) => {
      setFormValues({ ...updatedValues });
    },
  });

  // Return a merged object that satisfies both FormControls and DataSource
  return useMemo(() => {
    const buffer = bufferRef.current;
    return {
      // FormControls
      values: formValues,
      errors: formErrors,
      valid: formIsValid,
      dirty,
      submitting,
      loading: sourceLoading,
      setValue,
      setValues,
      submit,
      cancel,
      validateField,
      validateForm,
      // DataSource
      get value() {
        return buffer.value;
      },
      save: buffer.save.bind(buffer),
      get: buffer.get.bind(buffer) as DataSource<TForm>['get'],
      findBy: buffer.findBy.bind(buffer) as DataSource<TForm>['findBy'],
    };
  }, [
    formValues,
    formErrors,
    formIsValid,
    dirty,
    submitting,
    sourceLoading,
    setValue,
    setValues,
    submit,
    cancel,
    validateField,
    validateForm,
  ]);
}
